import { notifyError } from '@/store/utils';
import eventToMutation from '@/store/eventToMutation';
import indexedDB from '@/utils/indexedDB';
import { asyncFilter } from '@/utils/index';
import pako from 'pako';
import { sneakyPromise } from './profiling';
import { LONG_TASKS_SUPPORTED, mainThreadBusyMs } from '@/utils/mainThreadBusy';

const RECONNECT_INTERVAL_SECONDS = 5;
const CACHED_REQUESTS = [
  'getProjectAndSubscribe',
  'getProjectsListAndSubscribe',
  'getTaskAndSubscribe',
  'getWorkspaceAndSubscribe',
  'getDayoffsAndSubscribe',
  'getNotificationSettingsAndSubscribe',
  'getUiThemesAndSubscribe',
  'getProjectInfo', // to be able to create tasks in offline
];
const CACHE_TTL = 3600 * 24 * 5; // 5 days
const DATA_SIZE_LIMIT_TO_NOT_RETRY = 3 * 1024 * 1024;
const CACHE_CLEANUP_INTERVAL = 3600 * 1000; // 1 hour in milliseconds

// Cache cleanup scheduler
async function cleanupExpiredCache() {
  try {
    console.log('[CACHE] Starting cleanup of expired cache entries');
    const cleanedCount = await indexedDB.cleanupExpiredData({
      dbName: 'commonSpace',
    });
    if (cleanedCount > 0) {
      console.log(`[CACHE] Cleaned up ${cleanedCount} expired cache entries`);
    }
  } catch (error) {
    console.error('[CACHE] Error during cache cleanup:', error);
  }
}

// Start cache cleanup scheduler
let cacheCleanupInterval;
function startCacheCleanupScheduler() {
  if (!cacheCleanupInterval) {
    // Run cleanup immediately on first start
    setTimeout(() => {
      cleanupExpiredCache();
    }, 40_000); // 40 seconds delay for start initial cleanup

    // Schedule periodic cleanup
    cacheCleanupInterval = setInterval(cleanupExpiredCache, CACHE_CLEANUP_INTERVAL);
    console.log('[CACHE] Cache cleanup scheduler started');
  }
}

// Stop cache cleanup scheduler (useful for cleanup on app close)
function stopCacheCleanupScheduler() {
  if (cacheCleanupInterval) {
    clearInterval(cacheCleanupInterval);
    cacheCleanupInterval = null;
    console.log('[CACHE] Cache cleanup scheduler stopped');
  }
}

let connRecoverer;
window.tracklifyWebSocketState = {
  ws: null,
  pendingRequests: {},
  callQueue: [], // this is a sync single-threaded queue
  asyncPendingRequests: [], // this is async queue - queuing needed only to wait for connection, all requests go instantly
  connRecovererInterval: null,
  oneRequestAlreadyRunning: false,
  processCallQueue: null,
  lastConnectWasError: false,
  lastPong: Date.now(),
  // the server said there is no valid session: no reconnects until the user logs in
  authRequired: false,
};

const state = window.tracklifyWebSocketState;
console.log(`[WS] socket initialize tracklifyWebSocketState ${JSON.stringify(state)}`);

export const logitify = (params) => {
  try {
    const logP = JSON.stringify(params, null, 2);
    return logP ? JSON.parse(logP.replace(/"data:[a-z]+\/[a-z]+;base64,.+?"/g, '"<b64 stripped>"')) : logP;
  } catch (error) {
    console.warn('[WS] Unable to stringify params for log', error);
    return 'Error stringify';
  }
};

function decoupleObjects(params) {
  return params ? JSON.parse(JSON.stringify(params)) : params;
}

async function processCacheResolver() {
  state.callQueue = await asyncFilter(state.callQueue, item => item.cache && !item.cache.resolve());
}

const METHOD_NAME_STYLE = 'background: #ddd; color: #000; padding: 3px; border-radius: 3px;';

export const publishLocal = (eventItem, fromServer) => {
  if (eventItem.event !== 'updateStatusSyncing') {
    const size = humanSize(JSON.stringify(eventItem).length);
    lg(
      `> [${size}] ${fromServer ? '📢' : '🔄'} Received publish %c${eventItem.event}`,
      'color: orange; font-weight: bold;',
      `- ${fromServer ? 'server' : 'local'}:`, // eslint-disable-line
      decoupleObjects(eventItem),
      !eventToMutation[eventItem.event] ? '⛔ NO HANDLER' : '',
    ); // eslint-disable-line
  }
  if (!eventToMutation[eventItem.event]) {
    return;
  }
  eventToMutation[eventItem.event](eventItem.data);
};

const processCallQueue = () => {
  if (!state.ws || state.ws.readyState !== WebSocket.OPEN) {
    console.log('Attempt to process queue when socket not ready, skip');
    return;
  }
  if (state.oneRequestAlreadyRunning) {
    return;
  }
  const callItem = state.callQueue.shift();
  if (callItem) {
    const { call, persisted } = callItem;
    call();
    if (persisted) {
      publishLocal({ event: 'updateStatusSyncing', data: true });
    }
  }
};

function runAllAsyncPendingRequests() {
  if (!state.ws || state.ws.readyState !== WebSocket.OPEN) {
    console.log('Attempt to run async requests when socket not ready, skip');
    // serve what we can from cache
    const clonedAsyncPendingRequests = [...state.asyncPendingRequests];
    clonedAsyncPendingRequests.map(async ({ call, cache }) => {
      if (cache) {
        const response = await cache.resolve();
        if (response) {
          console.log('> 🗂 Response from cache => ', call.method)
          resolver(undefined, call.resolve, response, call.method);
          
          const indexInPending = state.asyncPendingRequests.findIndex(
            item => item.call === call,
          );
          if (indexInPending !== -1) {  
            state.asyncPendingRequests.splice(indexInPending, 1);
          }
        }
      }
    });
    return;
  }
  while (state.asyncPendingRequests.length > 0) {
    const asyncItem = state.asyncPendingRequests.shift();
    const { call } = asyncItem;
    call();
  }
};

const setDisconnect = (wasConnected = false) => {
  console.log('[WS] setDisconnect', wasConnected);
  try {
    // does not matter if ws is null or not, we will try close it anyway
    state.ws.close();
  } catch (e) {
  } finally {
    state.ws = null;
  }
  if (wasConnected) {
    console.log('[WS] Detected disconnect');
    state.oneRequestAlreadyRunning = false;
  }
  state.onDisconnect();
};

const setConnect = () => {
  console.log('[WS] Detected connect');
  state.lastPong = Date.now(); // give more time for next ping
};

// whether the login screen is shown; decided only by the server's answer about the session
export const setAuthRequired = (required) => {
  state.authRequired = required;
  // the socket of an ended session must not stay open (the next login may be another user); closed quietly, it is
  // not a lost connection
  if (required && state.ws) {
    state.ws.onclose = null;
    state.ws.close();
    state.ws = null;
  }
  publishLocal({ event: 'requireAuth', data: required });
};

const resolver = (uuid, resolve, data, parentUuid, method) => {
  try {
    resolve(data);
  } catch (e) {
    console.error(`> Error resolving call to ${method}`, e);
    // stack
    console.error(e.stack);
  }

  if (parentUuid && state.pendingRequests[parentUuid]) {
    try{
      state.pendingRequests[parentUuid].resolve(data);
    } catch (e) {
      console.error(`> Error resolving parent call to ${method} (parent ${state.pendingRequests[parentUuid]?.method})`, e);
      // stack
      console.error(e.stack);
    }
  }

  if (uuid && state.pendingRequests[uuid]) {
  // save data to cache if needed
    if (state.pendingRequests[uuid].cache) {
      indexedDB.saveData({
        data,
        dataId: state.pendingRequests[uuid].cache.key,
        dbName: 'commonSpace',
        timestamp: +new Date(+new Date() + CACHE_TTL),
      });
    }
    clearTimeout(state.pendingRequests[uuid].timeout);
    delete state.pendingRequests[uuid];
  }
};

export const callAPI = ({
  method,
  params,
  first,
  dontWaitIfNoConn,
  dontRetry,
  timeoutSeconds,
  parentUuidRequest,
  persisted,
  async, // async call will go instantly, without queue
}) => sneakyPromise((resolve, reject) => {
  const cache = (() => {
    if (!CACHED_REQUESTS.includes(method)) {
      return undefined;
    }

    const cacheKey = `_${method}_${JSON.stringify(params) ?? '_'}`;
    const rsl = async () => {
      const response = await indexedDB.readData({
        dataId: cacheKey,
        dbName: 'commonSpace',
        parse: true,
      });
      if (response) {
        console.log('> 🗂 Response from cache => ', method);
        resolver(undefined, resolve, response, parentUuidRequest, method);
        return true;
      }
      return false;
    };
    return {
      key: cacheKey,
      resolve: rsl,
    };
  })();

  // dontWaitIfNoConn means call will resolve instantly and crash if no connection
  // otherwise it will wait for connection to be established
  console.log(
    `> 📡 callAPI method: ${method}, first: ${first ? 'yes' : 'no'}, dontWaitIfNoConn: ${dontWaitIfNoConn || 'no'}, async: ${async ? 'yes' : 'no'}, ${state.ws?.readyState === WebSocket.OPEN ? 'ws open': 'ws readyState=' + state.ws?.readyState} params:`, 
    logitify(params)
  );
  const call = () => {
    const uuid = utils.genId();
    const parentUuid = parentUuidRequest || uuid;
    const sentData = JSON.stringify({
      method,
      params,
      version: 1.0,
      uid: uuid,
    });

    state.pendingRequests[uuid] = {
      resolve: data => resolver(uuid, resolve, data, parentUuidRequest, method),
      method,
      dontWaitIfNoConn,
      cache,
      startTime: performance.now(),
      timeout: setTimeout(() => {
        if (parentUuidRequest) {
          reject(new Error('TimeOut When child without retry'));
          delete state.pendingRequests[uuid];
        }
        state.oneRequestAlreadyRunning = false;
        console.log(`[WS] Timeout calling ${method}, retry`);

        if (dontRetry || sentData.length > DATA_SIZE_LIMIT_TO_NOT_RETRY) {
          if (sentData.length > DATA_SIZE_LIMIT_TO_NOT_RETRY) {
            console.error('Attempt to send too big data, will not retry >', DATA_SIZE_LIMIT_TO_NOT_RETRY);
          }
          resolver(uuid, resolve, { noConnectionError: false }, parentUuidRequest, method);
          return;
        }
        if (state.ws && state.ws.readyState !== WebSocket.OPEN && dontWaitIfNoConn) {
          resolver(uuid, resolve, { noConnectionError: true }, parentUuidRequest, method);
          return;
        }
        try {
          callAPI({
            method,
            params,
            first: true,
            dontWaitIfNoConn,
            dontRetry: undefined,
            timeoutSeconds: undefined,
            parentUuidRequest: parentUuid,
          });
        } catch (e) {
          console.log('>>> catch nested error >', e);
        }
      }, (timeoutSeconds || 15) * 1000),
    };
    state.oneRequestAlreadyRunning = state.pendingRequests[uuid];

    const reqSize = humanSize(sentData?.length || 0);
    console.log(`[${reqSize}] < Calling method  %c${method}`, METHOD_NAME_STYLE, params);

    state.ws.send(sentData);
  };
  
  if (state.ws && state.ws.readyState !== WebSocket.OPEN && dontWaitIfNoConn) {
    resolver(undefined, resolve, { noConnectionError: true }, parentUuidRequest, method);
    console.log('[WS] > No connection or socket not ready, will not call API');
    return;
  }
  if (!async) {
    if (first) {
      state.callQueue.unshift({ call, persisted, cache });
    } else {
      state.callQueue.push({ call, persisted, cache });
    }
  } else {
    // this is async call, but anyway we will put it to asyncPendingRequests
    if (first) {
      state.asyncPendingRequests.unshift({ call, persisted, cache });
    } else {
      state.asyncPendingRequests.push({ call, persisted, cache });
    }

  }
  if (!state.ws) {
    console.log(`No ws, postpone call for ${method}`);
    return;
  }
  if (state.ws.readyState !== WebSocket.OPEN) {
    console.log(`Socket not ready, postpone call for ${method}`);
    return;
  }
  if (state.callQueue.length > 0) {
    processCallQueue();
  }
  if (state.asyncPendingRequests.length > 0) {
    runAllAsyncPendingRequests();
  }
});

window.tracklifyWebSocketState.callAPI = callAPI;

const getEnvWsUrl = () => {
  if (process.env.VUE_APP_ENV === 'live') {
    return 'wss://ws1.tracklify.com/ws';
  }
  if (process.env.VUE_APP_ENV === 'dev') {
    return 'wss://ws1.devtracklify.com/ws';
  }
  return 'ws://tracklify.localhost:3102/ws';
};

function doPing() {
  if (state.ws && state.ws.readyState === WebSocket.OPEN) {
    try {
      state.ws.send(JSON.stringify({ ping: 1 }));
    } catch (e) {
      console.warn('Ping failed', e);
    }
  }
}

setInterval(() => {
  doPing();
}, 10000);

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // since in background browser may suspend interval, we need to reset lastPong
    state.lastPong = Date.now();
    doPing();  // and do ping quickly
  }
});



setInterval(() => {
  const diff = Date.now() - state.lastPong;
  if (diff > 20000) {
    if (state.ws && state.ws.readyState === WebSocket.OPEN) {
      console.warn('No pong received for 20 seconds, closing socket');
      // call on disconnect
      setDisconnect();
    }
  }
}, 5000);

window.addEventListener('offline', () => {
  console.warn('Browser offline');
  setDisconnect();
});



const humanSize = (size) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};  

export const connectSocket = ({
  onConnect, onTokenExpired, onDisconnect, onAccessDenied, imitationConnect,
}) => {

  if (!state.connRecovererInterval) {
    // initialize connRecovererInterval only once
    // at first connectSocket call
    state.connRecovererInterval = setInterval(() => connRecoverer(), RECONNECT_INTERVAL_SECONDS * 1000);
  }
  
  // Start cache cleanup scheduler
  startCacheCleanupScheduler();
  
  state.onConnect = onConnect;
  state.onTokenExpired = onTokenExpired;
  state.onDisconnect = onDisconnect;
  state.onAccessDenied = onAccessDenied;
  state.processCallQueue = processCallQueue;
  state.imitationConnect = imitationConnect;

  const wsUrl = getEnvWsUrl();
  console.log(`[WS] Connection attempt to ${wsUrl}`);

  if (state.ws) {
    console.warn('[WS] Socket already exists, closing it');
    try {
      state.ws.close();
    } catch (e) {
      console.warn('[WS] Error closing socket', e);
    }
  }
  state.ws = new WebSocket(`${wsUrl}?isApp=${window.APP_TRON ? 1 : 0}&appVersion=${('' + window.APP_TRON?.appVersion || 'unknown').toString('base64')}`);

  state.ws.onopen = () => {
    setConnect();
  };

  state.ws.onclose = (event) => {
    console.warn('[WS] Closed:', { 
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    }, event);
    setDisconnect(true);
  };

  state.ws.onerror = (e) => {
    console.log('[WS] Connect error, starting resolving from cache', e);
    state.lastConnectWasError = true;

    if (!state.isNotFirstConnect) {
      state.isNotFirstConnect = true;
      console.log('[WS] Imitation connect for cache resolve');
      state.imitationConnect();
    }

    // if we have pending requests, we need to resolve them from cache
    processCallQueue();
    runAllAsyncPendingRequests();
    setDisconnect();
  };

  state.ws.onmessage = async (event) => {

    const arrayBuffer = await event.data.arrayBuffer();
    const compressed = new Uint8Array(arrayBuffer);
    const jsonStr = pako.inflate(compressed, { to: 'string' });
    const data = JSON.parse(jsonStr);
    if (data.pong) {
      state.lastPong = Date.now();
      return;
    }
    if (data.TOKEN_VALID) {
      console.log('[WS] onmessage TOKEN_VALID');
      setAuthRequired(false);
      state.ws.id = data.connectionId; // set connectionId which was assigned on backend
      state.onConnect(!!state.isNotFirstConnect);
      state.isNotFirstConnect = true;
      state.lastConnectWasError = false;
      processCallQueue();
      runAllAsyncPendingRequests();
      return;
    }
    if (data.REFRESH_EXPIRED) {
      console.log('[WS] NO REFRESH TOKEN');
      console.log('[WS] NO REFRESH TOKEN COOKIE', data);
      setAuthRequired(true);
    }
    if (data.TOKEN_EXPIRED && state.onTokenExpired) {
      console.log('[WS] TOKEN EXPIRED');
      state.onTokenExpired();
      state.ws.close();
      return;
    }
    if (data.DENY && state.onAccessDenied) {
      console.log('[WS] ACCESS DENIED! =>', data);
      console.log('[WS] =⛔⛔⛔⛔ We will stop trying');

      state.onAccessDenied();
      clearInterval(state.connRecovererInterval);
      return;
    }
    if (!state.pendingRequests[data.uid]) {
      if (data.event) {
        const events = Array.isArray(data.event) ? data.event : [data.event];
        events.forEach(d => publishLocal(d, true));
      } else {
        console.log('[WS] Request not found for response', data);
      }
    } else {
      const { startTime } = state.pendingRequests[data.uid];
      const now = performance.now();
      const time = Math.round(now - startTime);
      const busy = mainThreadBusyMs(startTime, now);
      const blocked = LONG_TASKS_SUPPORTED
        ? ` | browser blocked ${Math.round(busy)}ms ${Math.round((busy / (now - startTime)) * 100)}%`
        : '';

      const size = jsonStr ? humanSize(jsonStr.length) : 'no data';

      console.log(
        `[${size}]> [%c${(time)}ms%c${blocked}] 📫 Got resp to %c${state.pendingRequests[data.uid].method}`,
        `font-weight: bold; color: ${time > 1000 ? 'red' : time > 300 ? 'orange' : 'green'}`,
        '',
        METHOD_NAME_STYLE,
        data.response,
      );
      state.oneRequestAlreadyRunning = false;
      state.pendingRequests[data.uid].resolve(data.response);
      if (state.callQueue.filter(({ persisted }) => persisted).length === 0) {
        publishLocal({ event: 'updateStatusSyncing', data: false });
      }
      // process next call in queue
      processCallQueue();
    }
  };
};

connRecoverer = () => {
  if (state.authRequired) {
    return;
  }
  if (!state.ws || state.ws.readyState !== WebSocket.OPEN) {
    console.log('[WS] =====> Try connect socket=');
    connectSocket({
      onConnect: state.onConnect,
      onTokenExpired: state.onTokenExpired,
      onDisconnect: state.onDisconnect,
      onAccessDenied: state.onAccessDenied,
      imitationConnect: state.imitationConnect,
    });
  }
};

// Export cleanup functions for external use if needed
export { cleanupExpiredCache, startCacheCleanupScheduler, stopCacheCleanupScheduler };
